import { Outlet, Navigate, useLocation } from "react-router-dom"
import { useEffect, useRef } from "react"
import useRefreshToken from "../../../hooks/useRefreshToken"
import { useAuth } from "../../../contexts/AuthContext"
import Error from "../../../layouts/Error"
import Loading from "../../../layouts/Loading"

export default function PersistLogin() {
    const refreshMutation = useRefreshToken()
    const { auth, persist, setAuthError } = useAuth()
    const location = useLocation()
    const refreshInitiated = useRef(false)

    // On logout auth gets updated to nothing which triggers this useEffect
    useEffect(() => {
        if (
            !auth?.token &&
            !auth?.logout &&
            persist &&
            !refreshInitiated.current &&
            !refreshMutation.isPending &&
            !refreshMutation.isSuccess &&
            !refreshMutation.isError
        ) {
            refreshInitiated.current = true
            refreshMutation.mutate()
        }
    }, [auth?.token, auth.logout, persist, refreshMutation])

    // Reset the flag when refresh completes
    useEffect(() => {
        if (refreshMutation.isSuccess || refreshMutation.isError) {
            refreshInitiated.current = false
        }
    }, [refreshMutation.isSuccess, refreshMutation.isError])

    // If not persisting, just render children
    if (!persist) {
        return <Outlet />
    }

    // If we have no token and not explicitly logged out, we're waiting for a refresh attempt
    // OR if refresh is in progress, show loading
    if (!auth?.token && !auth?.logout) {
        if (refreshMutation.isPending) {
            return <Loading />
        }

        if (refreshMutation.isError) {
            setAuthError(refreshMutation.error)
            return <Error error={refreshMutation.error} />
        }

        // Waiting for the first refresh attempt to possibly happen
        if (!refreshMutation.isSuccess) {
            return <Loading />
        }

        // Refresh was attempted but didn't get a token, redirect
        return <Navigate to="/" state={{ from: location }} replace />
    }

    // We have a token, render protected content
    return <Outlet />
}

